/**
 * CaseDock: the case file's footer grammar, in three pieces.
 *
 * A decision reads, left to right:
 *
 *   LABEL   one consequence sentence   [secondary verb] [PRIMARY]
 *
 * `FootLead` is the left half (the label and the sentence), handed to the
 * DecisionCard's foot so its verbs land to the right in DOM order. Once the
 * decision is made, `StampRow` takes the same row: the stamp, what happened,
 * and the page's own moves on (Next item). A state with nothing to decide is
 * a `DockRow`: quiet utilities on the left, the way forward on the right.
 * Archive is not a dock move: it lives in the drill-in header (HeaderArchive),
 * the one place every case file shows in every state, open decisions included.
 */
import type { ReactNode } from "react";

export function DockRow({
  utils,
  forward,
}: {
  /** Quiet utilities, left cluster. Null/absent → the row is forward-only. */
  utils?: ReactNode;
  /** The way forward, right cluster. */
  forward?: ReactNode;
}) {
  return (
    <div className="wk-dockrow">
      <div className="wk-dock-utils">{utils}</div>
      <div className="wk-dock-fwd">{forward}</div>
    </div>
  );
}

/** The label and the one consequence sentence, ahead of a decision's verbs. */
export function FootLead({ label, consequence }: { label: string; consequence: ReactNode }) {
  return (
    <>
      <span className="wk-foot-lbl" data-testid="wk-foot-label">
        {label}
      </span>
      <span className="wk-foot-cq" data-testid="wk-foot-consequence">
        {consequence}
      </span>
    </>
  );
}

/** A decision, made: the stamp, what it did, who and when, and the moves on. */
export function StampRow({
  stamp,
  text,
  by,
  acts,
}: {
  stamp: string;
  text: string;
  /** "by Ami · just now", when the page knows who acted. */
  by?: string | null;
  acts?: ReactNode;
}) {
  return (
    <div className="wk-stamprow" data-testid="wk-gate-stamp">
      <span className="wk-stamp is-acc">{stamp}</span>
      <span className="wk-foot-cq" data-testid="wk-gate-receipt">
        {text}
        {by ? <span className="wk-gate-stamp-by"> · {by}</span> : null}
      </span>
      {acts != null && <div className="wk-dock-fwd">{acts}</div>}
    </div>
  );
}
